import React, { useState, useEffect } from 'react';
import { Radio, Checkbox, Button, message, Progress, Space, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { levels, checkAnswer } from '../../utils/questionUtils';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { answerQuestion, updateQuizProgress, resetQuizProgress } from '../../store/userSlice';
import { STORAGE_KEYS, getFromStorage } from '../../utils/storage';
import styles from './Quiz.module.css';
import { RootState } from '../../store';
import siteConfig from '../../config/site.json';

const Quiz: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const savedProgress = useAppSelector((state: RootState) => ({
    currentLevel: state.user.currentLevel,
    currentQuestion: state.user.currentQuestion,
    score: state.user.score,
    answers: state.user.answers,
    errorCount: state.user.errorCount,
    answeredQuestions: state.user.answeredQuestions
  }));

  const [currentLevel, setCurrentLevel] = useState<number>(savedProgress.currentLevel);
  const [currentQuestion, setCurrentQuestion] = useState<number>(savedProgress.currentQuestion);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState<boolean>(false);
  const [score, setScore] = useState<number>(savedProgress.score);
  const [answers, setAnswers] = useState<number[]>(savedProgress.answers);
  const [isCorrect, setIsCorrect] = useState<boolean>(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [errorCount, setErrorCount] = useState<number>(savedProgress.errorCount);
  const [answeredQuestions, setAnsweredQuestions] = useState<number[]>(savedProgress.answeredQuestions);
  const [hasScored, setHasScored] = useState<boolean>(false);

  // 在状态改变时存进度
  useEffect(() => {
    dispatch(updateQuizProgress({
      currentLevel,
      currentQuestion,
      score,
      answers,
      errorCount,
      answeredQuestions
    }));
  }, [currentLevel, currentQuestion, score, answers, errorCount, answeredQuestions, dispatch]);

  useEffect(() => {
    // 从 localStorage 恢复状态
    const savedQuizState = getFromStorage(STORAGE_KEYS.QUIZ_STATE);
    if (savedQuizState) {
      dispatch(updateQuizProgress(savedQuizState));
    }
    
    // 如果没有用户信息，重定向到首页
    const savedUserInfo = getFromStorage(STORAGE_KEYS.USER_INFO);
    if (!savedUserInfo?.name) {
      navigate('/');
    }
  }, [dispatch, navigate]);

  const level = levels[currentLevel];
  const question = level.questions[currentQuestion];
  const isLastQuestion = currentQuestion === level.questions.length - 1;

  const handleSubmit = () => {
    if (question.type === 'single' && selectedAnswer === null) {
      message.warning(siteConfig.quiz.messages.selectAnswer);
      return;
    }
    if (question.type === 'multiple' && selectedAnswers.length === 0) {
      message.warning(siteConfig.quiz.messages.selectAnswers);
      return;
    }

    const correct = question.type === 'single' 
      ? selectedAnswer === question.correct
      : checkAnswer(question, selectedAnswers);

    setIsCorrect(correct);
    
    if (correct && !hasScored) {
      if (question.type === 'single') {
        setAnswers([...answers, selectedAnswer as number]);
      } else {
        setAnswers([...answers, ...selectedAnswers]);
      }
      message.success(siteConfig.quiz.messages.correct);
      setScore(score + question.score);  // 使用题目原本设定的分数
      setHasScored(true);
    } else if (!correct) {
      setErrorCount(errorCount + 1);
      if (errorCount + 1 >= 5) {
        message.error(siteConfig.quiz.messages.tooManyErrors);
        restartLevel();
        return;
      }
      message.error(siteConfig.quiz.messages.remainingChances.replace('{count}', String(4 - errorCount)));
    }

    setShowExplanation(true);
    dispatch(answerQuestion({ correct, difficulty: question.difficulty }));
  };

  const restartLevel = () => {
    setCurrentQuestion(0);
    const previousLevelsScore = currentLevel > 0 ? savedProgress.score : 0;
    setScore(previousLevelsScore);
    setAnswers([]);
    setErrorCount(0);
    setAnsweredQuestions([]);
    dispatch(updateQuizProgress({
      currentLevel,
      currentQuestion: 0,
      score: previousLevelsScore,
      answers: [],
      errorCount: 0,
      answeredQuestions: []
    }));
  };

  const clearProgress = () => {
    dispatch(resetQuizProgress());
  };

  const handleComplete = () => {
    clearProgress();
    message.success(siteConfig.quiz.messages.complete);
    navigate('/');
  };

  const handleNext = () => {
    if (!isCorrect) {
      setSelectedAnswer(null);
      setSelectedAnswers([]);
      setShowExplanation(false);
      return;
    }

    setAnsweredQuestions([...answeredQuestions, question.id]);

    if (isLastQuestion) {
      if (score >= level.requiredScore) {
        message.success(siteConfig.quiz.messages.success);
        if (currentLevel < levels.length - 1) {
          setCurrentLevel(currentLevel + 1);
          setCurrentQuestion(0);
          setAnswers([]);
          setErrorCount(0);
          setAnsweredQuestions([]);
        } else {
          handleComplete();
        }
      } else {
        message.error(siteConfig.quiz.messages.scoreNotEnough.replace('{required}', String(level.requiredScore)).replace('{current}', String(score)));
        restartLevel();
      }
    } else {
      const nextQuestion = level.questions.findIndex((q, index) => 
        index > currentQuestion && !answeredQuestions.includes(q.id)
      );
      setCurrentQuestion(nextQuestion);
    }
    
    setSelectedAnswer(null);
    setSelectedAnswers([]);
    setShowExplanation(false);
    setIsCorrect(false);
    setHasScored(false);
  };

  const handleExit = () => {
    setShowExitModal(true);
  };

  const confirmExit = () => {
    navigate('/');
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <Button 
            className={styles.exitButton}
            onClick={handleExit}
          >
            {siteConfig.quiz.buttons.exit}
          </Button>
          <h1>{siteConfig.quiz.title}</h1>
        </div>
        <div className={styles.progress}>
          <span>第 {currentLevel + 1} 关</span>
          <Progress 
            percent={Math.round(((currentQuestion + 1) / level.questions.length) * 100)} 
            format={() => `${currentQuestion + 1}/${level.questions.length}`}
            strokeColor="#ffd700"
          />
        </div>
        <div className={styles.scoreInfo}>
          <div className={styles.scoreItem}>
            <span>当前分数：</span>
            <span className={styles.scoreValue}>{score}</span>
          </div>
          <div className={styles.scoreItem}>
            <span>通关分数：</span>
            <span className={styles.scoreValue}>{level.requiredScore}</span>
          </div>
          <div className={styles.scoreItem}>
            <span>剩余机会：</span>
            <span className={styles.errorCount}>{5 - errorCount}</span>
          </div>
        </div>
      </div>

      <div className={styles.questionCard}>
        <div className={styles.questionTitle}>
          {question.title}
        </div>
        
        {question.type === 'single' ? (
          <Radio.Group 
            onChange={(e) => setSelectedAnswer(e.target.value)}
            value={selectedAnswer}
            disabled={showExplanation}
            className={styles.options}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {question.options.map((option, index) => (
                <Radio key={index} value={index} className={styles.option}>
                  {option}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        ) : (
          <Checkbox.Group
            onChange={(values) => setSelectedAnswers(values as number[])}
            value={selectedAnswers}
            disabled={showExplanation}
            className={styles.options}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {question.options.map((option, index) => (
                <Checkbox key={index} value={index} className={styles.option}>
                  {option}
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        )}

        {showExplanation && (
          <div className={styles.explanation}>
            <div className={isCorrect ? styles.correct : styles.wrong}>
              {isCorrect ? '答对了！' : '答错了！'}
            </div>
            <div className={styles.explanationText}>
              {question.explanation}
            </div>
          </div>
        )}

        <Button 
          type="primary"
          className={styles.actionButton}
          onClick={showExplanation ? handleNext : handleSubmit}
        >
          {!showExplanation ? siteConfig.quiz.buttons.submit : 
           (isCorrect ? (isLastQuestion ? siteConfig.quiz.buttons.complete : siteConfig.quiz.buttons.next) : siteConfig.quiz.buttons.retry)}
        </Button>
      </div>

      <Modal
        title={siteConfig.quiz.exitModalTitle}
        open={showExitModal}
        onOk={confirmExit}
        onCancel={() => setShowExitModal(false)}
        okText={siteConfig.quiz.buttons.confirm}
        cancelText={siteConfig.quiz.buttons.cancel}
      >
        <p>{siteConfig.quiz.exitModalContent}</p>
      </Modal>
    </div>
  );
};

export default Quiz; 